import { forwardRef } from "react";
import { cn } from "../lib/cn";

/**
 * Badge — count/dot indicator, MUI Badge semantics.
 *
 *   <BadgeAnchor count={3}>
 *     <Button size="icon" aria-label="Notifications"><NotificationsIcon /></Button>
 *   </BadgeAnchor>
 *
 *   <BadgeAnchor dot tone="error"><StatusDot /></BadgeAnchor>
 *
 * Hidden when there is nothing to say: no `dot` and a falsy/zero `count`
 * render nothing. Counts above `max` clamp to "99+" style labels.
 */

export interface BadgeProps {
  /** Number to display; 0 (or undefined without `dot`) hides the badge. */
  count?: number;
  /** Clamp point for the count label — 3 and 99 both render as "99+". */
  max?: number;
  /** Render a small indicator dot instead of the count. */
  dot?: boolean;
  tone?: "primary" | "error";
}

type BadgeTone = NonNullable<BadgeProps["tone"]>;

const toneClasses: Record<BadgeTone, string> = {
  primary: "bg-surface-primary text-on-primary",
  error: "bg-surface-error text-on-error",
};

/** MUI hides zero/empty badge content; a dot always shows. */
function isBadgeVisible({ dot, count }: BadgeProps): boolean {
  if (dot) {
    return true;
  }
  return count !== undefined && count > 0;
}

/** The pill/dot visual — no positioning; BadgeAnchor owns placement. */
function BadgeValue({ count, max = 99, dot, tone = "primary" }: BadgeProps) {
  if (dot) {
    return (
      <span
        className={cn("inline-block size-1.5 rounded-full", toneClasses[tone])}
      />
    );
  }
  return (
    <span
      className={cn(
        "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 type-caption font-medium leading-none",
        toneClasses[tone],
      )}
    >
      {count === undefined ? null : count > max ? `${max}+` : count}
    </span>
  );
}

/** Standalone inline badge — rare; prefer BadgeAnchor over controls. */
export function Badge(props: BadgeProps) {
  if (!isBadgeVisible(props)) {
    return null;
  }
  return <BadgeValue {...props} />;
}
Badge.displayName = "Badge";

export interface BadgeAnchorProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    BadgeProps {}

/** Relative wrapper that centres a badge on the child's top-right corner. */
export const BadgeAnchor = forwardRef<HTMLSpanElement, BadgeAnchorProps>(
  (
    { className, children, count, max, dot, tone, ...props },
    ref,
  ) => {
    const visible = isBadgeVisible({ count, max, dot, tone });
    return (
      <span
        ref={ref}
        className={cn("relative inline-flex", className)}
        {...props}
      >
        {children}
        {visible ? (
          <span className="pointer-events-none absolute left-full top-0 flex -translate-x-1/2 -translate-y-1/2">
            <BadgeValue count={count} max={max} dot={dot} tone={tone} />
          </span>
        ) : null}
      </span>
    );
  },
);
BadgeAnchor.displayName = "BadgeAnchor";
